/**
 * Shared utilities for prompt template section management.
 * Used by both AgentBuilder (save) and NodeConfigPanel (edit).
 */

export const AI_PROMPT_SECTION_KEYS = [
    'roleContext',
    'schemaContext',
    'businessRules',
    'validationChecklist',
    'preExecutionChecks',
    'errorHandling',
] as const;

export type PromptSectionKey = typeof AI_PROMPT_SECTION_KEYS[number];

export const AI_PROMPT_SECTION_LABELS: Record<PromptSectionKey, string> = {
    roleContext: 'Role Context',
    schemaContext: 'Schema Context',
    businessRules: 'Business Rules',
    validationChecklist: 'Validation Checklist',
    preExecutionChecks: 'Pre-Execution Checks',
    errorHandling: 'Error Handling',
};

export const AI_PROMPT_SECTION_FIELDS = [
    { key: 'roleContext' as PromptSectionKey, label: 'Role Context', placeholder: 'Who the agent is and its core behavior...' },
    { key: 'schemaContext' as PromptSectionKey, label: 'Schema Context', placeholder: 'Tables, joins, data model constraints...' },
    { key: 'businessRules' as PromptSectionKey, label: 'Business Rules', placeholder: 'Domain-specific formulas and strict rules...' },
    { key: 'validationChecklist' as PromptSectionKey, label: 'Validation Checklist', placeholder: 'Checks the agent must complete before returning output...' },
    { key: 'preExecutionChecks' as PromptSectionKey, label: 'Pre-Execution Checks', placeholder: 'Data availability and safety checks before execution...' },
    { key: 'errorHandling' as PromptSectionKey, label: 'Error Handling', placeholder: 'How the agent should handle and report failures...' },
];

/**
 * Compose a flat system-prompt string from structured sections.
 * Used at save time to produce the `systemPrompt` field for runtime.
 */
export const composeSystemPromptFromSections = (sections?: Record<string, string>): string => {
    if (!sections || typeof sections !== 'object') return '';
    const chunks: string[] = [];
    for (const key of AI_PROMPT_SECTION_KEYS) {
        const value = (sections[key] || '').trim();
        if (!value) continue;
        chunks.push(`${AI_PROMPT_SECTION_LABELS[key]}:\n${value}`);
    }
    return chunks.join('\n\n');
};

/**
 * Build a normalised sections object from a partial input,
 * ensuring all 6 keys are present (empty string for missing ones).
 */
export const normaliseSections = (raw?: Record<string, string>): Record<string, string> => {
    const existing = raw && typeof raw === 'object' ? raw : {};
    const result: Record<string, string> = {};
    for (const key of AI_PROMPT_SECTION_KEYS) {
        result[key] = existing[key] || '';
    }
    return result;
};
